// SkillsAISelector.jsx
import React from 'react';
import styled, { keyframes } from 'styled-components';

import TerminalBackground from '../assets/terminal-bg.jpg';

const slideInPlace = keyframes`
  0% { opacity: 0; transform: translateY(40px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Skills Data (kept your improved structure)
const skills = [
  {
    id: 'builder',
    name: "Builder",
    hint: "AI-generated custom shelter designs, detailed material lists, step-by-step build instructions, and site-specific construction plans adapted to your climate, soil, and available resources.",
    badgeColor: "#43fe47"
  },
  {
    id: 'water-systems',
    name: "Water Systems",
    hint: "Complete off-grid water system designs including rainwater catchment, storage sizing, gravity-fed distribution, filtration methods, and troubleshooting for contamination or low flow.",
    badgeColor: "#3b82f6"
  },
  {
    id: 'sanitation',
    name: "Sanitation",
    hint: "Custom humanure and composting toilet systems, waste management plans, odor control strategies, pathogen reduction protocols, and turning waste into safe garden fertilizer.",
    badgeColor: "#10b981"
  },
  {
    id: 'food-production',
    name: "Food Production",
    hint: "Personalized garden layouts, optimized crop rotation schedules, companion planting guides, soil amendment plans, pest management strategies, and realistic yield forecasts for your space and climate.",
    badgeColor: "#22c55e"
  },
  {
    id: 'repair-maintenance',
    name: "Repair & Maintenance",
    hint: "On-demand diagnostics for broken tools and systems, step-by-step repair guides, improvisation techniques using scrap materials, and preventive maintenance schedules to extend equipment life.",
    badgeColor: "#f59e0b"
  },
  {
    id: 'medic-basic',
    name: "Basic Medic",
    hint: "Interactive first-aid guides, symptom checkers, wound care protocols, injury prevention checklists, and triage decision trees tailored for off-grid or austere environments.",
    badgeColor: "#ef4444"
  },
  {
    id: 'food-preservation',
    name: "Food Preservation",
    hint: "Custom canning, dehydration, fermentation, and root cellar plans with altitude-adjusted safety guidelines, storage rotation schedules, and spoilage risk assessments.",
    badgeColor: "#8b5cf6"
  },
  {
    id: 'energy-fire',
    name: "Energy & Fire",
    hint: "Off-grid energy system sizing (solar, wind, micro-hydro), safe rocket stove and wood heat designs, fuel storage plans, and efficient lighting solutions with load calculations.",
    badgeColor: "#f97316"
  },
  {
    id: 'security',
    name: "Security",
    hint: "Strategic perimeter defense layouts, layered security plans, camera placement optimization, early warning systems, and deterrence strategies tailored to your property and threats.",
    badgeColor: "#64748b"
  },
  {
    id: 'inventory',
    name: "Inventory Management",
    hint: "Smart offline inventory tracking systems, automated consumption forecasting, expiration alerts, restocking recommendations, and efficient supply rotation to minimize waste.",
    badgeColor: "#14b8a6"
  },
  {
    id: 'communication',
    name: "Communication",
    hint: "Custom emergency signaling codes, radio and visual communication protocols, group coordination templates, and alert systems for when normal channels fail.",
    badgeColor: "#6366f1"
  },
  {
    id: 'navigation',
    name: "Navigation",
    hint: "Site-specific offline mapping tools, landmark-based navigation guides, route planning with risk assessment, and scouting strategies without relying on GPS.",
    badgeColor: "#0ea5e9"
  },
  {
    id: 'animal-husbandry',
    name: "Animal Husbandry",
    hint: "Custom feeding schedules, breeding plans, health monitoring checklists, disease prevention strategies, and livestock management systems for small-scale setups.",
    badgeColor: "#84cc16"
  },
  {
    id: 'tool-making',
    name: "Tool Making",
    hint: "Custom tool and jig designs from scrap materials, improvisation techniques, fabrication guides, and step-by-step instructions for building what you need with what you have.",
    badgeColor: "#f43f5e"
  },
  {
    id: 'mental-resilience',
    name: "Mental Resilience",
    hint: "Personalized stress management exercises, decision-making frameworks under pressure, scenario-based mental training, and daily resilience-building routines.",
    badgeColor: "#a855f7"
  },
  {
    id: 'foraging',
    name: "Foraging",
    hint: "Regional seasonal foraging calendars, safe plant identification guides, sustainable harvesting rules, preparation methods, and toxicity warnings for your local ecosystem.",
    badgeColor: "#16a34a"
  },
  {
    id: 'leadership',
    name: "Leadership",
    hint: "Team role assignment matrices, duty rosters, conflict resolution scripts, group decision-making frameworks, and leadership tools for high-stress group scenarios.",
    badgeColor: "#dc2626"
  },
  {
    id: 'beekeeping',
    name: "Beekeeping",
    hint: "Hive health monitoring systems, swarm prevention and prediction tools, optimal harvest timing, pest and disease management, and pollination efficiency plans.",
    badgeColor: "#ca8a04"
  },
  {
    id: 'primitive-skills',
    name: "Primitive Skills",
    hint: "Step-by-step bushcraft techniques, natural material improvisation guides, fire-by-friction methods, cordage making, shelter building, and survival skills adapted to your environment.",
    badgeColor: "#854d0e"
  },
  {
    id: 'advanced-medical',
    name: "Advanced Herbalism",
    hint: "Herb and plant identification tools, natural remedy recipes, safe tincture and salve preparation methods, dosage guidelines, and herbal first-aid kits tailored to your region.",
    badgeColor: "#b91c1c"
  }
];

const SkillsAISelector = () => {
  const handleInquiry = (skill) => {
    const subject = `Inquiry: AI Service - ${skill.name}`;
    window.location.href = `mailto:james@studiovoice2fly.com?subject=${encodeURIComponent(subject)}`;
  };

  return (
    <GridSection>
      <CardGrid>
        {skills.map((skill, index) => (
          <TerminalCard key={skill.id} index={index}>
            {/* <StatusBadge color={skill.badgeColor}>AI SERVICE</StatusBadge> */}

            <CardBody>
              <AppTitle>{skill.name}</AppTitle>
              <Description>{skill.hint}</Description>

              <InterfacePanel>
                <CommandBtn onClick={() => handleInquiry(skill)}>
                  [ INQUIRE ]
                </CommandBtn>
              </InterfacePanel>
            </CardBody>
          </TerminalCard>
        ))}
      </CardGrid>
    </GridSection>
  );
};

// ==================== STYLED COMPONENTS ====================

const GridSection = styled.section`
  padding: 90px 20px 100px;
  font-family: "VT323", monospace !important;
  background: transparent;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 28px;
  max-width: 1300px;
  margin: 0 auto;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 22px;
  }
`;

const TerminalCard = styled.div`
  background:
    linear-gradient(rgba(0, 20, 0, 0.68), rgba(0, 35, 0, 0.85)),
    url(${TerminalBackground}) center/cover no-repeat;

  border: 2px solid #1a331a;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 360px;
  overflow: hidden;
  transition: all 0.25s ease-in-out;

  opacity: 0;
  animation: ${slideInPlace} 0.6s ease-out forwards;
  animation-delay: ${props => props.index * 0.055}s;

  &:hover {
    border-color: #43fe47;
    box-shadow: 0 0 28px rgba(67, 254, 71, 0.45);
    transform: translateY(-6px);
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  background: ${props => props.color};
  color: #000;
  padding: 4px 12px;
  font-weight: bold;
  font-size: 0.9rem;
  z-index: 10;
  text-transform: uppercase;
  box-shadow: 2px 2px 0 #000;
`;

const CardBody = styled.div`
  padding: 32px 28px 28px;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
`;

const AppTitle = styled.h3`
  color: #fff;
  font-size: 1.48rem;
  margin-bottom: 14px;
  line-height: 1.3;
  text-transform: uppercase;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9);
`;

const Description = styled.p`
  color: #aaff9f;
  font-size: 1.08rem;
  line-height: 1.45;
  margin-bottom: 26px;
  flex: 1;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.85);
`;

const InterfacePanel = styled.div`
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #1a331a;
  position: relative;
  z-index: 2;
`;

const CommandBtn = styled.button`
  background: #43fe47;
  color: #000;
  border: 2px solid #43fe47;
  padding: 13px 20px;
  font-family: "VT323", monospace !important;
  font-size: 1.15rem;
  cursor: pointer;
  text-transform: uppercase;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background: #fff;
    color: #000;
    border-color: #fff;
    box-shadow: 0 0 18px rgba(67, 254, 71, 0.6);
  }
`;

export default SkillsAISelector;